
import { useState, useRef, useCallback } from "react";
import { Investor, Contact } from "@/types/investor";
import { toast } from "sonner";
import { saveToSupabaseThenLocal } from "@/utils/investors/databaseUtils";

/**
 * Hook for handling save operations
 * Uses optimized batch approach with database as single source of truth
 */
export const useSaveOperations = () => {
  const [isSaving, setIsSaving] = useState(false);
  const saveAttemptCount = useRef<number>(0);
  
  /**
   * Save data to Supabase with batch optimization
   */
  const saveDataToSupabaseAndLocal = useCallback(async (
    investors: Investor[], 
    contacts: Contact[],
    processedIds: Set<string>,
    forceSync = false
  ): Promise<boolean> => {
    try {
      console.log(`Saving ${investors.length} investors and ${contacts.length} contacts in optimized batches`);
      setIsSaving(true);
      
      // Basic validation
      if (!Array.isArray(investors) || investors.length === 0) {
        console.warn("No valid investors array to save");
        return false;
      }
      
      // Save to Supabase as the single source of truth with batch processing
      const result = await saveToSupabaseThenLocal(investors, contacts, processedIds, forceSync);
      
      if (result) {
        saveAttemptCount.current = 0;
        console.log("Successfully saved to Supabase");
        return true;
      } else {
        saveAttemptCount.current += 1;
        console.error("Failed to save to Supabase, attempt:", saveAttemptCount.current);
        
        if (saveAttemptCount.current > 3) {
          toast.error("Failed to save data after multiple attempts");
          saveAttemptCount.current = 0;
        }
        
        return false;
      }
    } catch (error) {
      console.error("Error in saveDataToSupabaseAndLocal:", error);
      return false;
    } finally {
      setIsSaving(false);
    }
  }, []);
  
  return {
    isSaving,
    saveDataToSupabaseAndLocal
  };
};
